<template>
  <div class="main">
    <!-- title -->
    <h3 class="title" content="edit" placement="bottom">
      IR Users Management
      <span class="search-toggle" @click="searchExpanded = !searchExpanded">
        {{ searchExpanded ? "Hide Filters" : "Show Filters" }}
        <em class="el-icon-arrow-up" :class="{ 'is-collapsed': !searchExpanded }"></em>
      </span>
    </h3>
    <!-- search -->
    <div class="searchBox" v-show="searchExpanded">
      <el-form :model="formInline" ref="formInline" class="demo-form-inline">
        <div class="search-grid">
          <el-form-item prop="lastName">
            <el-input
              v-model="formInline.lastName"
              placeholder="Last Name"
              @keyup.enter.native="submitSearch"
              maxlength="50"
            ></el-input>
          </el-form-item>
          <el-form-item prop="firstName">
            <el-input
              v-model="formInline.firstName"
              placeholder="First Name"
              @keyup.enter.native="submitSearch"
              maxlength="50"
            ></el-input>
          </el-form-item>
        </div>
      </el-form>
      <div class="search-toolbar">
        <div class="search-toolbar__actions">
          <el-button type="primary" class="search" @click="submitSearch">Search</el-button>
          <el-button class="reset" @click="resetForm('formInline')"
            >Reset</el-button
          >
        </div>
      </div>
    </div>
    <!-- toolbar, table & pagination share one panel -->
    <div class="client-panel">
      <div class="client-panel__toolbar">
        <columns-control
          :columns="columnsConfig"
          storage-key="cms.irManagement.columns"
          @change="onColumnsChange"
        ></columns-control>
      </div>
      <el-table :data="tableData">
        <el-table-column v-if="isColumnVisible('employeeId')" prop="employeeId" label="Employee ID" width="100">
          <template slot-scope="scope">{{
            scope.row.employeeId
          }}</template>
        </el-table-column>
        <el-table-column v-if="isColumnVisible('fullName')" label="Full Name">
          <template slot-scope="scope">{{
            scope.row.fullName
          }}</template>
        </el-table-column>
        <el-table-column v-if="isColumnVisible('email')" label="Email">
          <template slot-scope="scope">
            {{ scope.row.email }}
          </template>
        </el-table-column>
        <el-table-column v-if="isColumnVisible('phone')" label="Phone">
          <template slot-scope="scope">
            {{ scope.row.phone }}
          </template>
        </el-table-column>
        <el-table-column v-if="isColumnVisible('username')" label="Username">
          <template slot-scope="scope">
            {{ scope.row.username }}
          </template>
        </el-table-column>
        <el-table-column v-if="isColumnVisible('employeeStatus')" label="Employee Status">
          <template slot-scope="scope">
            {{ scope.row.employeeStatus }}
          </template>
        </el-table-column>
        <el-table-column v-if="isColumnVisible('status')" label="Status">
          <template slot-scope="scope">
            {{ formatStatus(scope.row.status) }}
          </template>
        </el-table-column>
        <el-table-column label="Operations" width="120">
          <template slot-scope="scope">
            <el-tooltip
              class="item"
              effect="dark"
              content="More"
              placement="top"
            >
              <el-dropdown
                trigger="click"
                @command="(command) => handleMore(command, scope.row)"
              >
                <span class="el-dropdown-link sortbox">
                  <em class="more"></em>
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="invite">Invite</el-dropdown-item>
                  <el-dropdown-item command="resetPassword">Reset password</el-dropdown-item>
                  <el-dropdown-item command="deactivate">Deactivate</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
      <!-- page -->
      <app-pagination
        @size-change="
          (v) => {
            currentSize = v;
            currentPage = 1;
            getList();
          }
        "
        @current-change="
          (v) => {
            currentPage = v;
            getList();
          }
        "
        :current-page="currentPage"
        :page-sizes="[5, 10, 20, 50]"
        :total="total"
        :page-size="currentSize"
      ></app-pagination>
    </div>

    <!-- invite dialog -->
    <el-dialog
      title="IR Account Detail"
      :visible.sync="inviteDialogVisible"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <el-form :model="inviteForm" label-position="left" label-width="120px">
        <el-form-item label="Full Name">
          <span>{{ inviteForm.fullName }}</span>
        </el-form-item>
        <el-form-item label="Email address">
          <el-input v-model="inviteForm.email" placeholder="Please enter email address"></el-input>
        </el-form-item>
        <el-form-item label="Phone number">
          <el-input
            v-model="inviteForm.phone"
            placeholder="Please enter phone number"
            @input="onInvitePhoneInput"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="inviteDialogVisible = false">Cancel</el-button>
        <el-button
          type="primary"
          :loading="inviteSubmitting"
          @click="submitInvite"
          >Send</el-button
        >
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  cmsIRList,
  irInvite,
  irDeactivate,
  irResetPassword,
  getSalesOffice,
  getSalesRegion,
} from "@/request/api";
export default {
  name: "IRUser",
  data() {
    return {
      showSave: true,
      formInline: {
        lastName: "",
        firstName: "",
      },
      tableData: [],
      currentSize: 10,
      currentPage: 1,
      sort: "userId,desc",
      total: 0,
      dialogUserVisible: false,
      formLabelWidth: "96px",
      formUser: {
        id: 0,
        username: "",
        password: "",
        roles: [],
      },
      formFilter: {
        salesOffice: "All",
        region: "All",
      },
      salesOfficeList: [],
      regionList: [],
      searchExpanded: true,
      columnsConfig: [
        { prop: "employeeId", label: "Employee ID", visible: true },
        { prop: "fullName", label: "Full Name", visible: true },
        { prop: "email", label: "Email", visible: true },
        { prop: "phone", label: "Phone", visible: true },
        { prop: "username", label: "Username", visible: true },
        { prop: "employeeStatus", label: "Employee Status", visible: true },
        { prop: "status", label: "Status", visible: true },
      ],
      visibleColumnProps: [
        "employeeId",
        "fullName",
        "email",
        "phone",
        "username",
        "employeeStatus",
        "status",
      ],
      inviteDialogVisible: false,
      inviteSubmitting: false,
      inviteForm: {
        employeeId: "",
        fullName: "",
        email: "",
        phone: "",
      },
    };
  },
  methods: {
    isColumnVisible(prop) {
      return this.visibleColumnProps.includes(prop);
    },
    formatStatus(status) {
      return status === "DELETED" ? "Deactivated" : status;
    },
    onColumnsChange(visibleProps) {
      this.visibleColumnProps = visibleProps;
    },
    showBtn(val) {
      console.log(this.$store.state.permissions);
      var permissions = this.$store.state.permissions.filter(
        (item) => item == val
      );
      if (!permissions[0]) {
        this.showSave = false;
      }
    },
    getFilterConfig() {
      getSalesOffice().then((res) => {
        this.salesOfficeList = res.data.payload;
      });
    },
    changeSalesOffice(v) {
      if (v == "All") {
        this.clearRegion();
      } else {
        this.clearRegion();
        getSalesRegion(v).then((res) => {
          this.regionList = res.data.payload;
        });
      }
    },
    clearRegion() {
      this.regionList = [];
      this.$set(this.formFilter, "region", "");
    },
    handleCommand(command) {
      this.sort = command;
      this.submitSearch();
    },
    handleMore(command, row) {
      if (command === "invite") {
        this.handleInvite(row);
      }
      if (command === "deactivate") {
        this.handleDeactivate(row);
      }
      if (command === "resetPassword") {
        this.handleResetPassword(row);
      }
    },
    handleInvite(row) {
      this.inviteForm = {
        employeeId: row.employeeId,
        fullName: row.fullName,
        email: row.email ? row.email : "",
        phone: row.phone ? row.phone.replace(/\D/g, "") : "",
      };
      this.inviteSubmitting = false;
      this.inviteDialogVisible = true;
    },
    onInvitePhoneInput(value) {
      this.inviteForm.phone = value.replace(/\D/g, "");
    },
    submitInvite() {
      const emailPattern =
        /[\w!#$%&'*+/=?^_`{|}~-]+(?:\.[\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\w](?:[\w-]*[\w])?\.)+[\w](?:[\w-]*[\w])?/;

      if (!emailPattern.test(this.inviteForm.email)) {
        this.$message.error("Please provide a properly formatted email");
        return;
      }

      if (!this.inviteForm.phone || !/^\d{7,15}$/.test(this.inviteForm.phone)) {
        this.$message.error("Please provide a valid mobile phone number");
        return;
      }

      const params = {
        email: this.inviteForm.email,
        employeeId: this.inviteForm.employeeId,
        phone: this.inviteForm.phone,
        cellularPhone: this.inviteForm.phone,
      };

      this.inviteSubmitting = true;
      irInvite(params)
        .then((res) => {
          this.inviteSubmitting = false;
          if (res.data.status == "00") {
            this.$message.success(
              "An activation email has been sent to the registered email address."
            );
            this.inviteDialogVisible = false;
            this.getList();
          } else if (res.data.msg.includes("Azure AD B2C")) {
            this.$message.error("Account already exists");
            this.inviteDialogVisible = false;
          } else {
            this.$message.error("Something went wrong");
          }
        })
        .catch(() => {
          this.inviteSubmitting = false;
        });
    },
    handleDeactivate(row) {
      this.$confirm(
        `Are you sure you want to deactivate the account for ${row.fullName}?`,
        "Deactivate Account",
        {
          confirmButtonText: "Deactivate",
          cancelButtonText: "Cancel",
          type: "warning",
          confirmButtonClass: "el-button--danger",
        }
      ).then(() => {
        irDeactivate({ employeeId: row.employeeId }).then((res) => {
          if (res.data.status == "00") {
            this.$message.success("Account has been deactivated successfully.");
            this.getList();
          } else {
            this.$message.error(res.data.msg || "Something went wrong");
          }
        });
      }).catch(() => {});
    },
    handleResetPassword(row) {
      this.$confirm(
        `Are you sure you want to reset the password for ${row.fullName}? A new temporary password will be generated and emailed to the user.`,
        "Reset Password",
        {
          confirmButtonText: "Reset",
          cancelButtonText: "Cancel",
          type: "warning",
          confirmButtonClass: "el-button--danger",
        }
      ).then(() => {
        irResetPassword({ employeeId: row.employeeId }).then((res) => {
          if (res.data.status == "00") {
            this.$message.success(
              "A new temporary password has been generated and emailed to the user."
            );
          } else {
            this.$message.error(res.data.msg || "Something went wrong");
          }
        });
      }).catch(() => {});
    },
    getList() {
      let params = {
        page: this.currentPage - 1,
        size: this.currentSize,
        sort: this.sort,
      };
      if (this.formInline.firstName)
        params["firstName"] = this.formInline.firstName;
      if (this.formInline.lastName)
        params["lastName"] = this.formInline.lastName;
      
      cmsIRList(params).then((res) => {
        console.log(res)
        this.tableData = res.data.content;
        this.total = res.data.totalElements;
      });
    },
    submitSearch() {
      this.currentPage = 1;
      this.getList();
    },
    resetForm(formName) {
      this.$refs[formName].resetFields();
      this.getList();
    },
  },
  created: function () {
    this.$emit("header", true);
    this.getList();
    this.getFilterConfig();
    this.showBtn("CONTENT_WRITE");
  },
  beforeRouteLeave(to, from, next) {
    if (to.name == "cmsEditUser") {
      from.meta.keepAlive = true;
    } else {
      from.meta.keepAlive = false;
    }
    next();
  },
  activated() {
    if (this.$route.params.update) {
      this.getList();
    }
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.search-toggle {
  display: inline-flex;
  align-items: center;
  column-gap: 4px;
  float: right;
  margin-right: 20px;
  line-height: 40px;
  font-size: 14px;
  font-weight: 600;
  color: #003448;
  cursor: pointer;
}
.search-toggle .el-icon-arrow-up {
  transition: transform 0.2s ease;
}
.search-toggle .el-icon-arrow-up.is-collapsed {
  transform: rotate(180deg);
}

.searchBox {
  padding: 20px;
  margin-bottom: 20px;
  background: #fff;
  border: 1px solid #e6ebee;
  border-radius: 8px;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 20px;
}

.search-grid /deep/ .el-form-item {
  margin-bottom: 16px;
}

.search-grid /deep/ .el-input__inner {
  width: 100%;
}

.search-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 12px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #e6ebee;
}

.search-toolbar__actions .el-button {
  min-width: 96px;
}

/* Toolbar, table & pagination merged into one panel instead of three separate cards. */
.client-panel {
  background: #fff;
  border: 1px solid #e6ebee;
  border-radius: 8px;
  overflow: hidden;
}

.client-panel__toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 14px 20px;
  border-bottom: 1px solid #e6ebee;
}

.client-panel /deep/ .el-table {
  border: none;
  border-radius: 0;
}

.client-panel /deep/ .app-pagination {
  margin-top: 0;
  border: none;
  border-radius: 0;
  border-top: 1px solid #e6ebee;
}
</style>



// WEBPACK FOOTER //
// src/components/portalIRUser/portalIRUser.vue